/**
 * Voice Lock hook — manages speaker verification state.
 *
 * Three modes:
 * - Train: opens own MicVAD + AnalyserNode, collects speech segments,
 *          extracts embeddings, saves average as enrolled profile
 * - Test: opens own MicVAD + AnalyserNode, scores each utterance against profile
 * - Live: runs alongside normal VAD, scoring each utterance passively
 *
 * Training and Testing have INDEPENDENT audio capture — they do NOT rely
 * on the main voice pipeline's VAD being active.
 *
 * Live scoring always runs when a voice profile is enrolled — there is no
 * separate toggle. The audio gate (`scoreAudioForGate`) is controlled by
 * `gateEnabled` and blocks unrecognized voices independently.
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { MicVAD } from '@ricky0123/vad-web';
import {
  SpeakerVerificationManager,
  SPEAKER_MODELS,
  getSelectedModelKey,
  profileKey,
  trainedAtKey,
} from '../voice/speakerVerification';
import { STORAGE_KEYS } from '../utils/constants';
import { remoteLog } from '../utils/remoteLog';

// ── Threshold Defaults ──
const DEFAULT_THRESHOLD = 0.5;
const THRESHOLD_MIN = 0.3;
const THRESHOLD_MAX = 0.9;
const THRESHOLD_STEP = 0.05;

/** Read threshold from localStorage. */
function getStoredThreshold(): number {
  const raw = localStorage.getItem(STORAGE_KEYS.VOICE_LOCK_THRESHOLD);
  if (raw !== null) {
    const val = parseFloat(raw);
    if (!isNaN(val) && val >= THRESHOLD_MIN && val <= THRESHOLD_MAX) return val;
  }
  return DEFAULT_THRESHOLD;
}

export { SPEAKER_MODELS, DEFAULT_THRESHOLD, THRESHOLD_MIN, THRESHOLD_MAX, THRESHOLD_STEP };

// ── Asset paths (same as vad.ts) ──
const VAD_BASE_ASSET_PATH = import.meta.env.BASE_URL + 'vad/';
const ONNX_WASM_BASE_PATH = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.22.0/dist/';

// ── Singleton Manager ──
// Shared across hook instances to avoid multiple ONNX model loads.
let managerInstance: SpeakerVerificationManager | null = null;

function getManager(): SpeakerVerificationManager {
  if (!managerInstance) {
    managerInstance = new SpeakerVerificationManager();
  }
  return managerInstance;
}

// ── Public Interface ──

// ── Sentence pool for guided training prompts ──
const TRAINING_SENTENCE_POOL = [
  // Pangrams & phoneme-rich
  'The quick brown fox jumps over the lazy dog',
  'Pack my box with five dozen liquor jugs',
  'The five boxing wizards jump quickly',
  'She sells seashells by the seashore',
  'How much wood would a woodchuck chuck',
  'Peter Piper picked a peck of pickled peppers',
  'A proper copper coffee pot is a pretty good gift',
  'Fuzzy Wuzzy was a bear but Fuzzy Wuzzy had no hair',
  'Unique New York, you know you need unique New York',
  'Red lorry, yellow lorry, red lorry, yellow lorry',
  // Everyday sentences
  'My voice is my passport, verify me',
  'The weather today looks absolutely wonderful',
  'I enjoy listening to music on rainy afternoons',
  'Please call me back when you get this message',
  'Technology makes our lives easier every day',
  'Every morning starts with a fresh cup of coffee',
  'A journey of a thousand miles begins with a single step',
  'Good things come to those who wait patiently',
  'Reading books opens doors to new worlds',
  'Stars shine brightest on clear winter nights',
  // Descriptive & varied
  'The old oak tree stood tall against the evening sky',
  'I left my umbrella on the train this morning',
  'Can you pick up some milk on your way home',
  'The restaurant around the corner has amazing pasta',
  'We should plan a weekend trip to the mountains',
  'The sunset painted the clouds in shades of orange and pink',
  'My favorite season is autumn when the leaves change color',
  'The dog chased its tail around the living room',
  'I need to remember to water the plants before Friday',
  'Traffic was lighter than usual on the highway today',
  'The concert last night was absolutely incredible',
  'She finished the marathon in under four hours',
  'The library closes at nine on weekday evenings',
  'Fresh bread from the bakery smells wonderful',
  'I wonder if it will snow before the end of the week',
];

/** Pick `count` random non-repeating items from an array */
function pickRandom<T>(pool: T[], count: number): T[] {
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

export interface UseVoiceLockReturn {
  /** Whether the audio gate is enabled (blocks unrecognized voices) */
  gateEnabled: boolean;
  /** Has a trained voice profile */
  isEnrolled: boolean;
  /** Currently in training flow */
  isTraining: boolean;
  /** Currently in additive training mode */
  isAdditiveTraining: boolean;
  /** Currently in test mode */
  isTesting: boolean;
  /** Whether the model is initializing */
  isInitializing: boolean;
  /** Latest Live mode similarity score */
  liveScore: number | null;
  /** Latest Test mode similarity score */
  testScore: number | null;
  /** When the profile was trained (ISO string) */
  trainedAt: string | null;
  /** Number of training samples collected so far */
  trainingSampleCount: number;
  /** Current audio level (0.0-1.0) when mic is active during training/testing */
  audioLevel: number;
  /** True when enough samples have been collected */
  trainingComplete: boolean;
  /** Error message if training failed */
  trainingError: string | null;
  /** The prompted sentences for the current training session */
  promptedSentences: string[];
  /** Latest adaptive training score for current sample */
  adaptiveScore: number | null;
  /** All adaptive training scores for the session */
  adaptiveScores: number[];

  // ── Model picker ──
  /** Currently selected model key */
  selectedModel: string;
  /** Change the speaker verification model (clears profile) */
  changeModel: (key: string) => Promise<void>;

  // ── Threshold ──
  /** Current similarity threshold */
  threshold: number;
  /** Update the threshold */
  setThreshold: (val: number) => void;

  // ── Verification ──
  /** Score from post-training verification (null if not yet run) */
  verificationScore: number | null;
  /** Verification status */
  verificationStatus: 'idle' | 'listening' | 'passed' | 'failed';
  /** Sentence used for verification */
  verificationSentence: string | null;

  /** Toggle the audio gate on/off */
  setGateEnabled: (val: boolean) => void;
  /** Begin training — opens own mic + VAD */
  startTraining: () => void;
  /** Begin additive training — collects additional samples to strengthen profile */
  startAdditiveTraining: () => void;
  /** Finalize training — stop mic, process samples, enroll */
  stopTraining: () => void;
  /** Open test overlay — opens own mic + VAD */
  enterTestMode: () => void;
  /** Close test overlay — releases mic */
  exitTestMode: () => void;
  /** Delete enrolled profile */
  clearProfile: () => void;
  /** Called by main VAD when speech ends — feeds audio to Live mode only */
  onVADSpeechEnd: (audio: Float32Array) => void;
  /** Score audio and return whether it should be processed (gate). Returns true if gate is disabled or not enrolled. */
  scoreAudioForGate: (audio: Float32Array) => Promise<boolean>;
}

// ── Required training samples ──
const REQUIRED_SAMPLES = 5;
const MAX_ADAPTIVE_SAMPLES = 15;

export function useVoiceLock(): UseVoiceLockReturn {
  // ── State ──
  const [gateEnabled, setGateEnabledState] = useState(
    () => localStorage.getItem(STORAGE_KEYS.VOICE_LOCK_GATE_ENABLED) === 'true',
  );
  const [isEnrolled, setIsEnrolled] = useState(() => {
    const modelKey = getSelectedModelKey();
    return localStorage.getItem(profileKey(modelKey)) !== null;
  });
  const [isTraining, setIsTraining] = useState(false);
  const [isAdditiveTraining, setIsAdditiveTraining] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [liveScore, setLiveScore] = useState<number | null>(null);
  const [testScore, setTestScore] = useState<number | null>(null);
  const [trainedAt, setTrainedAt] = useState<string | null>(() => {
    const modelKey = getSelectedModelKey();
    return localStorage.getItem(trainedAtKey(modelKey));
  });
  const [trainingSampleCount, setTrainingSampleCount] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [trainingComplete, setTrainingComplete] = useState(false);
  const [trainingError, setTrainingError] = useState<string | null>(null);
  const [promptedSentences, setPromptedSentences] = useState<string[]>([]);
  const [adaptiveScore, setAdaptiveScore] = useState<number | null>(null);
  const [adaptiveScores, setAdaptiveScores] = useState<number[]>([]);

  // ── Model & Threshold State ──
  const [selectedModel, setSelectedModel] = useState<string>(getSelectedModelKey);
  const [threshold, setThresholdState] = useState<number>(getStoredThreshold);

  // ── Verification State ──
  const [verificationScore, setVerificationScore] = useState<number | null>(null);
  const [verificationStatus, setVerificationStatus] = useState<'idle' | 'listening' | 'passed' | 'failed'>('idle');
  const [verificationSentence, setVerificationSentence] = useState<string | null>(null);

  // ── Refs ──
  // Keep threshold in a ref so the gate callback doesn't have a stale closure
  const thresholdRef = useRef(threshold);
  thresholdRef.current = threshold;

  // Keep gateEnabled in a ref for use in callbacks
  const gateEnabledRef = useRef(gateEnabled);
  gateEnabledRef.current = gateEnabled;

  const trainingSamplesRef = useRef<Float32Array[]>([]);
  const managerRef = useRef<SpeakerVerificationManager>(getManager());
  const isVerifyingRef = useRef(false);
  const usedTrainingSentencesRef = useRef<string[]>([]);
  const isAdditiveTrainingRef = useRef(false);
  // Dynamic required sample count — 5 for fresh training, 3 for additive
  const requiredSamplesRef = useRef(REQUIRED_SAMPLES);

  // Wake lock ref — keeps screen on during training
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  // Independent mic capture refs
  const micVadRef = useRef<MicVAD | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const levelFrameRef = useRef<number>(0);
  // Track current mode in refs so VAD callbacks can read them without stale closures
  const isTrainingRef = useRef(false);
  const isTestingRef = useRef(false);

  // ── Wake Lock Helpers ──
  const acquireWakeLock = useCallback(async () => {
    if (wakeLockRef.current) return; // already held
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        wakeLockRef.current.addEventListener('release', () => {
          wakeLockRef.current = null;
        });
        remoteLog('voicelock', 'wake_lock_acquired');
      }
    } catch (err) {
      // Wake lock not supported or failed — continue without it
      remoteLog('voicelock', 'wake_lock_failed', { error: String(err) });
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
      } catch {
        // ignore release errors
      }
      wakeLockRef.current = null;
      remoteLog('voicelock', 'wake_lock_released');
    }
  }, []);

  // ── Re-acquire wake lock on visibility change ──
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && (isTrainingRef.current || isVerifyingRef.current)) {
        await acquireWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [acquireWakeLock]);

  // ── Ensure model is initialized when needed ──
  const ensureInit = useCallback(async () => {
    const mgr = managerRef.current;
    if (mgr.isReady) {
      // Model already loaded — just sync enrolled state in case it's stale
      setIsEnrolled(mgr.isEnrolled);
      if (mgr.isEnrolled) {
        setTrainedAt(localStorage.getItem(trainedAtKey(getSelectedModelKey())));
      }
      return;
    }
    setIsInitializing(true);
    try {
      await mgr.init();
      // Sync enrolled state after init (model may have loaded profile from localStorage)
      setIsEnrolled(mgr.isEnrolled);
      if (mgr.isEnrolled) {
        setTrainedAt(localStorage.getItem(trainedAtKey(getSelectedModelKey())));
      }
    } catch (err) {
      throw err;
    } finally {
      setIsInitializing(false);
    }
  }, []);

  // ── Audio Level Polling ──
  const startLevelPolling = useCallback(() => {
    const poll = () => {
      const analyser = analyserRef.current;
      if (!analyser) {
        setAudioLevel(0);
        return;
      }
      const data = new Uint8Array(analyser.fftSize);
      analyser.getByteTimeDomainData(data);
      // Compute RMS
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / data.length);
      // Scale to 0-1 range (RMS of speech is typically 0.05-0.3)
      const level = Math.min(1, rms / 0.25);
      setAudioLevel(level);
      levelFrameRef.current = requestAnimationFrame(poll);
    };
    levelFrameRef.current = requestAnimationFrame(poll);
  }, []);

  const stopLevelPolling = useCallback(() => {
    if (levelFrameRef.current) {
      cancelAnimationFrame(levelFrameRef.current);
      levelFrameRef.current = 0;
    }
    setAudioLevel(0);
  }, []);

  // ── Independent Mic Capture ──
  const startMicCapture = useCallback(async (mode: 'training' | 'testing') => {
    // Get mic stream
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const msg = 'Microphone access denied. Please allow microphone access and try again.';
      if (mode === 'training') {
        setTrainingError(msg);
        setIsTraining(false);
        isTrainingRef.current = false;
      }
      remoteLog('voicelock', 'mic_access_denied', { mode, error: String(err) });
      throw new Error(msg);
    }
    micStreamRef.current = stream;

    // Set up AudioContext + AnalyserNode for level metering
    const audioCtx = new AudioContext();
    audioCtxRef.current = audioCtx;
    const source = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(analyser);
    analyserRef.current = analyser;

    // Start level polling
    startLevelPolling();

    // Create MicVAD that reuses the same mic stream
    try {
      const vad = await MicVAD.new({
        getStream: () => Promise.resolve(stream),
        positiveSpeechThreshold: 0.7,
        negativeSpeechThreshold: 0.25,
        minSpeechMs: 300,
        preSpeechPadMs: 300,
        baseAssetPath: VAD_BASE_ASSET_PATH,
        onnxWASMBasePath: ONNX_WASM_BASE_PATH,
        onSpeechEnd: (audio: Float32Array) => {
          if (isTrainingRef.current) {
            trainingSamplesRef.current.push(audio);
            const count = trainingSamplesRef.current.length;
            setTrainingSampleCount(count);

            // For additive training: score against current profile in real time
            const mgrInner = managerRef.current;
            if (isAdditiveTrainingRef.current && mgrInner.isReady && mgrInner.isEnrolled) {
              mgrInner.scoreAudio(audio)
                .then((score: number) => {
                  setAdaptiveScore(score);
                  setAdaptiveScores(prev => {
                    const updated = [...prev, score];
                    // Check stopping conditions
                    const currentThreshold = getStoredThreshold();
                    // Condition 1: Last 2 scores above threshold → good enough
                    if (updated.length >= 2) {
                      const lastTwo = updated.slice(-2);
                      if (lastTwo.every(s => s >= currentThreshold)) {
                        setTrainingComplete(true);
                      }
                    }
                    // Condition 2: No improvement after 3 consecutive sentences
                    if (updated.length >= 3) {
                      const lastThree = updated.slice(-3);
                      const firstOfThree = lastThree[0];
                      const improving = lastThree.some(s => s > firstOfThree + 0.02);
                      if (!improving) {
                        setTrainingComplete(true);
                      }
                    }
                    return updated;
                  });
                  remoteLog('voicelock', 'adaptive_score', { score, sampleIndex: count - 1 });
                })
                .catch((err: unknown) => console.warn('[useVoiceLock] Adaptive scoring failed:', err));

              // Hard cap for additive training
              if (count >= MAX_ADAPTIVE_SAMPLES) {
                setTrainingComplete(true);
              }
            }

            // For fresh training: fixed count
            if (!isAdditiveTrainingRef.current && count >= requiredSamplesRef.current) {
              setTrainingComplete(true);
            }

            remoteLog('voicelock', 'training_sample_collected', {
              count,
              audioLength: audio.length,
              additive: isAdditiveTrainingRef.current,
            });
          } else if (isTestingRef.current) {
            const mgr = managerRef.current;
            if (mgr.isReady && mgr.isEnrolled) {
              mgr.scoreAudio(audio)
                .then((score) => {
                  setTestScore(score);
                  remoteLog('voicelock', 'test_score', { score });
                })
                .catch((err) => {
                  console.warn('[useVoiceLock] Test scoring failed:', err);
                });
            }
          }
        },
        onSpeechStart: () => {
          // No-op — we just need onSpeechEnd
        },
      });
      micVadRef.current = vad;
      await vad.start();
      remoteLog('voicelock', 'mic_capture_started', { mode });
    } catch (err) {
      // Clean up on failure
      stopLevelPolling();
      analyserRef.current = null;
      audioCtx.close().catch(() => {});
      audioCtxRef.current = null;
      stream.getTracks().forEach(t => t.stop());
      micStreamRef.current = null;

      const msg = 'Failed to start voice detection. Please try again.';
      if (mode === 'training') {
        setTrainingError(msg);
        setIsTraining(false);
        isTrainingRef.current = false;
      }
      remoteLog('voicelock', 'mic_vad_init_failed', { mode, error: String(err) });
      throw new Error(msg);
    }
  }, [startLevelPolling, stopLevelPolling]);

  const stopMicCapture = useCallback(async () => {
    // Stop level polling
    stopLevelPolling();

    // Stop and destroy MicVAD
    if (micVadRef.current) {
      try {
        await micVadRef.current.pause();
        await micVadRef.current.destroy();
      } catch {
        // ignore cleanup errors
      }
      micVadRef.current = null;
    }

    // Close audio context
    if (audioCtxRef.current) {
      try {
        await audioCtxRef.current.close();
      } catch {
        // ignore
      }
      audioCtxRef.current = null;
      analyserRef.current = null;
    }

    // Stop mic stream tracks
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(t => t.stop());
      micStreamRef.current = null;
    }

    remoteLog('voicelock', 'mic_capture_stopped');
  }, [stopLevelPolling]);

  // ── Toggle (audio gate) ──
  const setGateEnabled = useCallback((val: boolean) => {
    setGateEnabledState(val);
    if (val) {
      localStorage.setItem(STORAGE_KEYS.VOICE_LOCK_GATE_ENABLED, 'true');
      // Lazy-init the model when enabling the gate
      void ensureInit();
    } else {
      localStorage.removeItem(STORAGE_KEYS.VOICE_LOCK_GATE_ENABLED);
    }
  }, [ensureInit]);

  // ── Threshold ──
  const setThreshold = useCallback((val: number) => {
    setThresholdState(val);
    localStorage.setItem(STORAGE_KEYS.VOICE_LOCK_THRESHOLD, String(val));
  }, []);

  // ── Model Change ──
  const changeModel = useCallback(async (key: string) => {
    const mgr = managerRef.current;
    setIsInitializing(true);
    try {
      await mgr.changeModel(key);
      setSelectedModel(key);
      // Sync enrolled state for the new model (it may already have a profile!)
      setIsEnrolled(mgr.isEnrolled);
      setTrainedAt(mgr.isEnrolled
        ? localStorage.getItem(trainedAtKey(key))
        : null);
      setLiveScore(null);
      setTestScore(null);
      remoteLog('voicelock', 'model_changed', { model: key, hasProfile: mgr.isEnrolled });
    } catch (err) {
      console.error('[useVoiceLock] Failed to change model:', err);
    } finally {
      setIsInitializing(false);
    }
  }, []);

  // ── Training (fresh) ──
  const startTraining = useCallback(() => {
    trainingSamplesRef.current = [];
    setTrainingSampleCount(0);
    setTrainingComplete(false);
    setTrainingError(null);
    setVerificationScore(null);
    setVerificationStatus('idle');
    setVerificationSentence(null);
    setAdaptiveScore(null);
    setAdaptiveScores([]);
    isVerifyingRef.current = false;
    isAdditiveTrainingRef.current = false;
    setIsAdditiveTraining(false);
    requiredSamplesRef.current = REQUIRED_SAMPLES;

    const sentences = pickRandom(TRAINING_SENTENCE_POOL, REQUIRED_SAMPLES);
    setPromptedSentences(sentences);
    usedTrainingSentencesRef.current = sentences;
    setIsTraining(true);
    isTrainingRef.current = true;

    void acquireWakeLock();

    // Ensure model is ready, then start mic capture
    void ensureInit()
      .then(() => startMicCapture('training'))
      .catch((err) => {
        console.error('[useVoiceLock] Failed to start training mic:', err);
        // Error state already set in startMicCapture
      });

    remoteLog('voicelock', 'training_started');
  }, [ensureInit, startMicCapture, acquireWakeLock]);

  // ── Training (additive — add more samples to existing profile) ──
  const startAdditiveTraining = useCallback(() => {
    trainingSamplesRef.current = [];
    setTrainingSampleCount(0);
    setTrainingComplete(false);
    setTrainingError(null);
    setVerificationScore(null);
    setVerificationStatus('idle');
    setVerificationSentence(null);
    setAdaptiveScore(null);
    setAdaptiveScores([]);
    isVerifyingRef.current = false;
    isAdditiveTrainingRef.current = true;
    setIsAdditiveTraining(true);
    requiredSamplesRef.current = MAX_ADAPTIVE_SAMPLES;

    const sentences = pickRandom(TRAINING_SENTENCE_POOL, Math.min(MAX_ADAPTIVE_SAMPLES, TRAINING_SENTENCE_POOL.length));
    setPromptedSentences(sentences);
    usedTrainingSentencesRef.current = sentences;
    setIsTraining(true);
    isTrainingRef.current = true;

    void acquireWakeLock();

    // Ensure model is ready, then start mic capture
    void ensureInit()
      .then(() => startMicCapture('training'))
      .catch((err) => {
        console.error('[useVoiceLock] Failed to start additive training mic:', err);
      });

    remoteLog('voicelock', 'additive_training_started');
  }, [ensureInit, startMicCapture, acquireWakeLock]);

  /** Start verification mic capture — listens for one utterance and scores it. */
  const startVerificationCapture = useCallback(async () => {
    isVerifyingRef.current = true;
    setVerificationStatus('listening');

    // Pick a verification sentence not used during training
    const usedSet = new Set(usedTrainingSentencesRef.current);
    const available = TRAINING_SENTENCE_POOL.filter(s => !usedSet.has(s));
    const sentence = available.length > 0
      ? available[Math.floor(Math.random() * available.length)]
      : 'Please say any sentence in your normal voice';
    setVerificationSentence(sentence);

    // Get mic stream
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      setVerificationStatus('failed');
      isVerifyingRef.current = false;
      void releaseWakeLock();
      remoteLog('voicelock', 'verify_mic_denied', { error: String(err) });
      return;
    }
    micStreamRef.current = stream;

    // Audio context for level metering
    const audioCtx = new AudioContext();
    audioCtxRef.current = audioCtx;
    const source = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(analyser);
    analyserRef.current = analyser;
    startLevelPolling();

    try {
      const vad = await MicVAD.new({
        getStream: () => Promise.resolve(stream),
        positiveSpeechThreshold: 0.7,
        negativeSpeechThreshold: 0.25,
        minSpeechMs: 300,
        preSpeechPadMs: 300,
        baseAssetPath: VAD_BASE_ASSET_PATH,
        onnxWASMBasePath: ONNX_WASM_BASE_PATH,
        onSpeechEnd: async (audio: Float32Array) => {
          if (!isVerifyingRef.current) return;
          isVerifyingRef.current = false;

          const mgr = managerRef.current;
          try {
            const score = await mgr.scoreAudio(audio);
            setVerificationScore(score);
            const currentThreshold = getStoredThreshold();
            if (score >= currentThreshold) {
              setVerificationStatus('passed');
              remoteLog('voicelock', 'verify_passed', { score, threshold: currentThreshold });
            } else {
              setVerificationStatus('failed');
              remoteLog('voicelock', 'verify_failed', { score, threshold: currentThreshold });
            }
          } catch (err) {
            setVerificationStatus('failed');
            remoteLog('voicelock', 'verify_error', { error: String(err) });
          }

          // Release wake lock — training + verification flow is complete
          if (wakeLockRef.current) {
            try { await wakeLockRef.current.release(); } catch { /* ignore */ }
            wakeLockRef.current = null;
            remoteLog('voicelock', 'wake_lock_released');
          }

          // Stop mic capture after one sample
          stopLevelPolling();
          if (micVadRef.current) {
            try {
              await micVadRef.current.pause();
              await micVadRef.current.destroy();
            } catch { /* ignore */ }
            micVadRef.current = null;
          }
          if (audioCtxRef.current) {
            try { await audioCtxRef.current.close(); } catch { /* ignore */ }
            audioCtxRef.current = null;
            analyserRef.current = null;
          }
          if (micStreamRef.current) {
            micStreamRef.current.getTracks().forEach(t => t.stop());
            micStreamRef.current = null;
          }
        },
        onSpeechStart: () => { /* no-op */ },
      });
      micVadRef.current = vad;
      await vad.start();
      remoteLog('voicelock', 'verify_mic_started');
    } catch (err) {
      stopLevelPolling();
      analyserRef.current = null;
      audioCtx.close().catch(() => {});
      audioCtxRef.current = null;
      stream.getTracks().forEach(t => t.stop());
      micStreamRef.current = null;
      setVerificationStatus('failed');
      isVerifyingRef.current = false;
      void releaseWakeLock();
      remoteLog('voicelock', 'verify_vad_init_failed', { error: String(err) });
    }
  }, [startLevelPolling, stopLevelPolling, releaseWakeLock]);

  const stopTraining = useCallback(async () => {
    const additive = isAdditiveTrainingRef.current;
    isTrainingRef.current = false;
    setIsTraining(false);

    // Stop mic capture first
    await stopMicCapture();

    const samples = trainingSamplesRef.current;
    trainingSamplesRef.current = [];

    if (samples.length === 0) {
      setTrainingError('No speech detected. Make sure your microphone is working and speak clearly.');
      setTrainingSampleCount(0);
      setTrainingComplete(false);
      void releaseWakeLock();
      remoteLog('voicelock', 'training_aborted', { reason: 'no_samples', additive });
      return;
    }

    try {
      const mgr = managerRef.current;
      await ensureInit();

      if (additive) {
        // Additive: combine new samples with existing embeddings
        await mgr.enrollAdditional(samples);
        // Force reload the enrolled embedding from localStorage to ensure
        // the in-memory profile matches what was just saved
        mgr.loadEnrolledProfile();
        remoteLog('voicelock', 'additive_training_complete', { newSampleCount: samples.length });
        // Reload the page to re-initialize all state cleanly
        window.location.reload();
        return;
      } else {
        // Fresh enrollment
        await mgr.enroll(samples);
        // Force reload the enrolled embedding from localStorage to ensure
        // the in-memory profile matches what was just saved
        mgr.loadEnrolledProfile();
        remoteLog('voicelock', 'training_complete', { sampleCount: samples.length });
      }

      setIsEnrolled(true);
      setTrainedAt(localStorage.getItem(trainedAtKey(getSelectedModelKey())));
      setTrainingError(null);

      // Start verification phase (fresh training only — additive reloads above)
      await startVerificationCapture();
    } catch (err) {
      console.error('[useVoiceLock] Training failed:', err);
      setTrainingError('Failed to save voice profile. Please try again.');
      void releaseWakeLock();
      remoteLog('voicelock', 'training_error', { error: String(err), additive });
    }
  }, [ensureInit, stopMicCapture, startVerificationCapture, releaseWakeLock]);

  // ── Test Mode ──
  const enterTestMode = useCallback(() => {
    setTestScore(null);
    setIsTesting(true);
    isTestingRef.current = true;

    void ensureInit()
      .then(() => startMicCapture('testing'))
      .catch((err) => {
        console.error('[useVoiceLock] Failed to start test mic:', err);
        setIsTesting(false);
        isTestingRef.current = false;
      });

    remoteLog('voicelock', 'test_mode_entered');
  }, [ensureInit, startMicCapture]);

  const exitTestMode = useCallback(async () => {
    isTestingRef.current = false;
    setIsTesting(false);
    setTestScore(null);

    await stopMicCapture();

    remoteLog('voicelock', 'test_mode_exited');
  }, [stopMicCapture]);

  // ── Clear Profile ──
  const clearProfile = useCallback(() => {
    managerRef.current.clearProfile();
    setIsEnrolled(false);
    setTrainedAt(null);
    setLiveScore(null);
    setTestScore(null);
  }, []);

  // ── VAD Speech End Handler (for LIVE mode only) ──
  // This is called from the main voice pipeline whenever VAD detects speech end.
  // Training and testing now use their own independent mic capture.
  //
  // NOTE: When the audio gate (scoreAudioForGate) is active, it already scores
  // the audio and updates liveScore. In that case, skip scoring here to avoid
  // scoring twice. The gate is always wired when gateEnabled is true, so we
  // only score here as a fallback for cases where the hook is used without the gate.
  const onVADSpeechEnd = useCallback((audio: Float32Array) => {
    const mgr = managerRef.current;

    // When the gate is active (gateEnabled + ready + enrolled), scoreAudioForGate
    // already handles scoring + liveScore update. Skip here to avoid double-scoring.
    if (gateEnabledRef.current && mgr.isReady && mgr.isEnrolled) return;

    // Otherwise, score for display if enrolled
    if (!mgr.isReady || !mgr.isEnrolled) return;

    mgr.scoreAudio(audio)
      .then((score) => {
        setLiveScore(score);
      })
      .catch((err) => {
        console.warn('[useVoiceLock] Live scoring failed:', err);
      });
  }, []);

  // ── Audio Gate ──
  // Scores audio against the enrolled voice profile and returns whether it should
  // be processed (sent to STT). Returns true (allow) if the gate is disabled,
  // not enrolled, or if the model isn't ready. Fails open on errors.
  const scoreAudioForGate = useCallback(async (audio: Float32Array): Promise<boolean> => {
    const mgr = managerRef.current;
    // If the gate is off or model not ready → allow everything
    if (!gateEnabledRef.current || !mgr.isReady) return true;

    // Ensure profile is loaded (defensive — handles edge cases like reconnect)
    if (!mgr.isEnrolled) {
      mgr.loadEnrolledProfile();
      if (!mgr.isEnrolled) return true;
    }

    try {
      const score = await mgr.scoreAudio(audio);
      // Update live score display
      setLiveScore(score);
      remoteLog('voicelock', 'gate_score', {
        score,
        threshold: thresholdRef.current,
        passed: score >= thresholdRef.current,
      });
      return score >= thresholdRef.current;
    } catch (err) {
      console.warn('[useVoiceLock] Gate scoring failed, allowing audio:', err);
      return true; // Fail open — don't block audio if scoring breaks
    }
  }, []);

  // ── Cleanup on unmount ──
  useEffect(() => {
    return () => {
      // Release mic if component unmounts during training/testing
      isTrainingRef.current = false;
      isTestingRef.current = false;
      void stopMicCapture();
      // Release wake lock on unmount
      if (wakeLockRef.current) {
        void wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    };
  }, [stopMicCapture]);

  // ── Initialize model on mount if a voice profile exists or gate is enabled ──
  useEffect(() => {
    const modelKey = getSelectedModelKey();
    const gateOn = localStorage.getItem(STORAGE_KEYS.VOICE_LOCK_GATE_ENABLED) === 'true';
    const hasProfile = localStorage.getItem(profileKey(modelKey)) !== null;

    setIsEnrolled(hasProfile);
    if (hasProfile) {
      setTrainedAt(localStorage.getItem(trainedAtKey(modelKey)));
    }

    // Load the ONNX model if there's a profile to use or the gate is enabled
    if (hasProfile || gateOn) {
      void ensureInit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMemo(() => ({
    gateEnabled,
    isEnrolled,
    isTraining,
    isAdditiveTraining,
    isTesting,
    isInitializing,
    liveScore,
    testScore,
    trainedAt,
    trainingSampleCount,
    audioLevel,
    trainingComplete,
    trainingError,
    promptedSentences,
    adaptiveScore,
    adaptiveScores,
    selectedModel,
    changeModel,
    threshold,
    setThreshold,
    verificationScore,
    verificationStatus,
    verificationSentence,
    setGateEnabled,
    startTraining,
    startAdditiveTraining,
    stopTraining,
    enterTestMode,
    exitTestMode,
    clearProfile,
    onVADSpeechEnd,
    scoreAudioForGate,
  }), [
    gateEnabled,
    isEnrolled,
    isTraining,
    isAdditiveTraining,
    isTesting,
    isInitializing,
    liveScore,
    testScore,
    trainedAt,
    trainingSampleCount,
    audioLevel,
    trainingComplete,
    trainingError,
    promptedSentences,
    adaptiveScore,
    adaptiveScores,
    selectedModel,
    changeModel,
    threshold,
    setThreshold,
    verificationScore,
    verificationStatus,
    verificationSentence,
    setGateEnabled,
    startTraining,
    startAdditiveTraining,
    stopTraining,
    enterTestMode,
    exitTestMode,
    clearProfile,
    onVADSpeechEnd,
    scoreAudioForGate,
  ]);
}
